import { ComboboxData, ComboboxItem, SelectProps } from "@mantine/core"
import { useDebouncedValue } from "@mantine/hooks"
import { UseQueryResult } from "@tanstack/react-query"
import { PaginationType } from "@tfg/types/schemas/utils"
import { useEffect, useState } from "react"

import { Select } from "../../../molecules/Inputs/Select/Select"

type AutocompleteProps<T> = {
  value: string | null
  onChange: (newValue: string | null, option: ComboboxItem) => void
  onCreate: (query: string) => void
  parseOptionFunction: (data: T[]) => {
    label: string
    value: string
  }[]
  pageSize?: number
  isLoading?: boolean
  query: (
    args: PaginationType
  ) => Pick<
    UseQueryResult<T[], unknown>,
    "data" | "isLoading" | "isFetching" | "isError" | "error"
  >
} & SelectProps

const PAGE_SIZE = 20

export const AutoCompleteWithQuery = <T,>({
  onCreate,
  onChange,
  value,
  query,
  parseOptionFunction,
  pageSize = PAGE_SIZE,
  ...props
}: AutocompleteProps<T>) => {
  const [selectOptions, setSelectOptions] = useState<ComboboxData>([])
  const [search, setSearch] = useState<string>()
  const [debouncedSearch] = useDebouncedValue(search, 300)

  const { data, isLoading: isQueryLoading } = query({
    searchParams: debouncedSearch,
    pagination: {
      page: 0,
      pageSize,
    },
  })

  useEffect(() => {
    if (!isQueryLoading && data) {
      const parsedOptions = parseOptionFunction(data) || []
      setSelectOptions(parsedOptions)
    }
  }, [data, parseOptionFunction, isQueryLoading])

  return (
    <Select
      data={selectOptions}
      withAsterisk={props.required}
      isLoading={isQueryLoading}
      creatable
      searchable
      searchValue={search}
      clearable
      onSearchChange={(value) => {
        setSearch(value)
      }}
      onCreate={(query?: string | null) => onCreate(query ?? "")}
      onChange={(newValue, option) => {
        if (onChange) {
          onChange(newValue, option)
        }
      }}
      value={value}
      {...props}
    />
  )
}
